/**
 * drawlol: app shell entry (M1.T7 + M2 fix B1a).
 *
 * Mounts Preact into #app (replacing SSR scaffold on hydration).
 * Uses preact-iso for routing:
 *   /        → Splash (landing: Create a game + Join a game)
 *   /about   → About (the "what is this" page + the Free Draw CTA, item 1)
 *   /r/:id   → RoomShell (FreeDrawRoom or game stub based on WS state)
 */

import { render } from "preact";
import { LocationProvider, Router, Route } from "preact-iso";
import { useEffect, useState } from "preact/hooks";
import { startWsClient, type ConnState } from "./ws.js";
import { Splash } from "./routes/Splash.js";
import { RoomShell } from "./routes/RoomShell.js";
import { About } from "./routes/About.js";
import { NotFound } from "./routes/NotFound.js";
import { bootstrapLatencyProbe } from "./perf/probeBootstrap.js";
import { LatencyHUD } from "./perf/LatencyHUD.js";

const CLIENT_VERSION = "0.1.0";

/**
 * WS URL resolution order:
 *   1. `import.meta.env.VITE_WS_URL` if set at build time, production
 *      deploy (GCE) bakes `wss://drawlol.com/ws` into the bundle
 *      via Vite env so the browser doesn't have to guess.
 *   2. SSR / test (no `window`), `ws://localhost:8787/ws`.
 *   3. Browser dev (http), `ws://<host>:8787/ws` (Fastify's dev port).
 *   4. Browser prod-ish (https), `wss://<host>/ws` (nginx terminates TLS
 *      and proxies `/ws` to the server on the same host, no port suffix).
 */
function resolveWsUrl(): string {
  const fromEnv = import.meta.env["VITE_WS_URL"];
  if (typeof fromEnv === "string" && fromEnv.length > 0) return fromEnv;
  if (typeof window === "undefined") return "ws://localhost:8787/ws";
  const isSecure = window.location.protocol === "https:";
  const proto = isSecure ? "wss" : "ws";
  // M3.fix B10: respect the current page's port so e2e/static servers
  // proxying /ws on a non-8787 port work. Vite dev mode (port 5173) also
  // works because Vite proxies /ws to the Fastify server. Only fall back
  // to the hardcoded :8787 for the bare Fastify HTML shell at port 8787.
  const host = window.location.host; // "127.0.0.1:55699" or "drawlol.com"
  return `${proto}://${host}/ws`;
}

const WS_URL = resolveWsUrl();

/**
 * Context shared from the WS connection to child routes.
 * Routes subscribe via the onState callback passed down.
 */
export type AppState = {
  connState: ConnState;
};

// M5.T13: bootstrap the latency probe once at module load, it returns a
// no-op handle unless the user has opted in via `?latency=1` or the
// localStorage flag, so this is free for regular play sessions.
const latencyProbe = bootstrapLatencyProbe();

export function App() {
  const [connState, setConnState] = useState<ConnState>({ kind: "connecting" });

  useEffect(() => {
    const { close } = startWsClient({
      url: WS_URL,
      clientVersion: CLIENT_VERSION,
      onState: setConnState,
    });
    return close;
  }, []);

  return (
    <LocationProvider>
      <Router>
        <Route path="/" component={() => <Splash connState={connState} />} />
        <Route path="/about" component={() => <About connState={connState} />} />
        <Route
          path="/r/:id"
          component={RoomShell}
          connState={connState}
          wsUrl={WS_URL}
          clientVersion={CLIENT_VERSION}
        />
        {/* Default route (audit 2026-06-11): unknown paths used to render a
            blank cream page; bare 4-char codes forward to /r/CODE. */}
        <Route default component={NotFound} />
      </Router>
      {latencyProbe.enabled && <LatencyHUD probe={latencyProbe} />}
    </LocationProvider>
  );
}

// Mount into #app: replaces the SSR scaffold on hydration.
// The SSR HTML uses <div id="app"> as the mount target (§13.10).
//
// Preact's `render()` does NOT auto-remove existing children, it appends the
// new tree alongside them, which causes the SSR wordmark + CTAs to coexist
// with the Preact-rendered Splash (visible as a duplicate <h1>drawlol</h1>).
// Clear the SSR scaffold before mounting so only the Preact tree remains.
const root = document.getElementById("app");
if (root) {
  root.replaceChildren();
  render(<App />, root);
}
